import { Button, LoadingSpinner, Modal, NavigationTab, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { HiDocumentDuplicate, HiDocumentText, HiFolder } from "react-icons/hi";
import { MdChevronRight } from "react-icons/md";
import { useInView } from "react-intersection-observer";

import { useSelection } from "@/contexts/SelectionContext";
import { useDuplicateLesson } from "@/lib/hooks/lessons/useDuplicateLesson";
import { useProjectNavigation } from "@/lib/hooks/modal/useProjectNavigation";
import { useDuplicateProject } from "@/lib/hooks/projects/useDuplicateProject";

interface DuplicateLessonModalProps {
  isOpen: boolean;
  itemType?: "lesson" | "project";
  defaultNavigationStack?: Array<{ id: string; name: string }> | undefined;
}

export function DuplicateItemModal({
  isOpen,
  itemType = "lesson",
  defaultNavigationStack,
}: DuplicateLessonModalProps) {
  const [activeTab, setActiveTab] = useState("projects");
  const [searchText, setSearchText] = useState("");
  const [nameError, setNameError] = useState("");
  const [duplicateType, setDuplicateType] = useState<"unique" | "linked">("unique");

  const {
    currentProjectId,
    projects,
    projectsLoading,
    hasMoreProjects,
    currentParent,
    navigationStack,
    handleProjectClick,
    navigateToIndex,
    resetNavigation,
    loadMoreProjects,
  } = useProjectNavigation(searchText, defaultNavigationStack);

  const {
    selectedLessonIds,
    selectedProjectIds,
    isDuplicateLessonModalOpen,
    isDuplicateProjectModalOpen,
    toggleDuplicateLessonModal,
    toggleDuplicateProjectModal,
    clearAllSelections,
  } = useSelection();

  const defaultName =
    itemType === "lesson" ? isDuplicateLessonModalOpen || "" : isDuplicateProjectModalOpen || "";

  const [newItemName, setNewItemName] = useState(defaultName);

  const { duplicateLesson, loading: duplicateLessonLoading } = useDuplicateLesson();
  const { duplicateProject, loading: duplicateProjectLoading } = useDuplicateProject();

  // Ref for the scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tabs = [{ id: "projects", label: "projects" }];

  const validateName = (name: string): string => {
    if (!name.trim()) {
      return "Name is required";
    }
    if (name.length > 100) {
      return "Name must be 100 characters or less";
    }
    return "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewItemName(value);
    setNameError(validateName(value));
  };

  const isNameValid = newItemName.trim() && newItemName.length <= 100;

  const handleDuplicate = async () => {
    if (!isNameValid) return;

    try {
      if (itemType === "lesson") {
        const lessonId = selectedLessonIds[0];
        if (lessonId) {
          await duplicateLesson(lessonId, currentProjectId, newItemName.trim(), duplicateType);
        }
      } else {
        const projectId = selectedProjectIds[0];
        if (projectId) {
          await duplicateProject(projectId, currentProjectId, newItemName.trim(), duplicateType);
        }
      }
      handleClose();
    } catch (error) {
      console.error(`Failed to duplicate ${itemType}:`, error);
    }
  };

  const handleClose = () => {
    resetNavigation();
    setSearchText("");
    setActiveTab("projects");
    setNewItemName("");
    setNameError("");

    if (itemType === "lesson") {
      toggleDuplicateLessonModal(false);
    } else {
      toggleDuplicateProjectModal(false);
    }

    clearAllSelections();
  };

  // Infinite scroll triggers
  const { ref: projectsLoadMoreRef, inView: projectsInView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });

  // Auto-load projects when scrolling
  useEffect(() => {
    if (activeTab === "projects" && projectsInView && hasMoreProjects && !projectsLoading) {
      loadMoreProjects();
    }
  }, [activeTab, projectsInView, hasMoreProjects, projectsLoading, loadMoreProjects]);

  if (!isOpen) return null;

  const modalTitle = itemType === "lesson" ? "Duplicate Lesson" : "Duplicate Project";
  const itemLabel = itemType === "lesson" ? "Lesson" : "Project";
  const isLoading = duplicateLessonLoading || duplicateProjectLoading;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={modalTitle}>
      <div className="space-y-4 relative">
        <p className="text-sm text-secondary-200">
          Do you want to create a <strong>Unique</strong> or a <strong>Linked</strong> copy?
        </p>

        {/* <p className="text-sm text-secondary-200">
          <strong>Unique:</strong> A new {itemLabel.toLowerCase()} with all its{" "}
          {itemType === "lesson" ? "slides" : "children (projects, lessons, and slides)"} will be
          created. Changes to the original {itemLabel.toLowerCase()} will not affect the duplicated{" "}
          {itemLabel.toLowerCase()}.
        </p>

        <p className="text-sm text-secondary-200">
          <strong>Linked:</strong> A copy of this {itemLabel.toLowerCase()} will be made. Changes to
          the original {itemLabel.toLowerCase()} will also be applied to the linked{" "}
          {itemLabel.toLowerCase()}. (Not yet implemented)
        </p> */}

        {/* Item Name Input */}
        <div className="flex w-full gap-3 justify-between">
          <div className="space-y-1 flex-1">
            <label className="label flex items-center gap-1">
              <span className="text-red-500">*</span> Duplicate {itemLabel} Name
            </label>
            <input
              type="text"
              value={newItemName}
              onChange={handleNameChange}
              placeholder="Enter slide name"
              className="w-full bg-surface border border-gray-800 rounded-md px-3 py-2 text-sm text-secondary-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            {nameError && <p className="text-xs text-red-500">{nameError}</p>}
          </div>

          {/* Duplicate Type Dropdown */}
          <div className="space-y-1">
            <label className="label flex items-center gap-1">
              <span className="text-red-500">*</span> Duplication Type
            </label>

            <select
              value={duplicateType}
              onChange={(e) => setDuplicateType(e.target.value as "unique" | "linked")}
              className="w-full bg-surface border border-gray-800 rounded-md px-3 py-2 text-sm text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="unique">Unique</option>
              <option value="linked">Linked</option>
            </select>
          </div>
        </div>

        <p className="label flex items-center gap-1">
          <span className="text-red-500">*</span> Duplicate destination
        </p>

        <SearchBar
          value={searchText}
          onChange={setSearchText}
          placeholder="Search projects, lessons..."
        />
        <NavigationTab tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        <nav
          aria-label="Folder breadcrumb"
          className="flex items-center gap-1 text-sm min-w-0 overflow-hidden flex-wrap"
        >
          <button
            type="button"
            onClick={() => navigateToIndex(-1)}
            className={`flex-shrink-0 font-medium transition-colors ${
              navigationStack.length === 0
                ? "text-secondary-200 cursor-default"
                : "text-gray-400 hover:text-primary-500"
            }`}
          >
            Home
          </button>
          {navigationStack.map((item, i) => {
            const isLast = i === navigationStack.length - 1;
            return (
              <span key={item.id} className="flex items-center gap-1 min-w-0">
                <MdChevronRight className="w-4 h-4 flex-shrink-0 text-gray-600" />
                <button
                  type="button"
                  onClick={() => navigateToIndex(i)}
                  className={`truncate font-medium transition-colors ${
                    isLast
                      ? "text-secondary-200 cursor-default"
                      : "text-gray-400 hover:text-primary-500"
                  }`}
                >
                  {item.name}
                </button>
              </span>
            );
          })}
        </nav>
        <div
          ref={scrollContainerRef}
          className="h-64 overflow-y-auto border border-gray-800 rounded-lg gradient-scrollbar"
        >
          <>
            {/* Root Level Option */}
            {!currentParent && (
              <button
                type="button"
                onClick={() => handleProjectClick(null)}
                className={`w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-800 transition-colors rounded
                  ${currentProjectId === null ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}`}
              >
                <div className="flex items-center gap-3">
                  <HiFolder className="w-5 h-5 text-secondary-200" />
                  <div className="font-medium text-secondary-200">Root / Home</div>
                </div>
                {currentProjectId === null && (
                  <span className="text-xs font-semibold text-primary-500">Selected</span>
                )}
              </button>
            )}

            {projectsLoading && projects.length === 0 ? (
              <div className="p-4 flex gap-2 flex-col items-center justify-center h-full caption">
                <LoadingSpinner />
                <p className="animate-pulse">Loading...</p>
              </div>
            ) : projects.length === 0 && currentParent ? (
              <div className="p-4 flex gap-2 flex-col items-center justify-center h-full caption">
                <HiDocumentDuplicate className="w-10 h-10 text-secondary-200" />
                Click "Duplicate" to create a duplicate in this folder.
              </div>
            ) : (
              <>
                {activeTab === "projects" &&
                  projects.map((project) => {
                    const isDisabled = project.isLinkedCopy || project.hasLinkedCopy || isLoading;
                    const isSelected = currentProjectId === project.id;
                    return (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() =>
                          !isDisabled && handleProjectClick({ id: project.id, name: project.name })
                        }
                        disabled={isDisabled}
                        className={`w-full flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800 last:border-b-0 transition-colors rounded
                          ${isSelected ? "bg-primary-500/10 ring-2 ring-primary-500" : ""}
                          ${
                            isDisabled
                              ? "bg-gray-800/20 cursor-not-allowed opacity-60"
                              : "hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <HiFolder
                            className={`w-5 h-5 flex-shrink-0 ${isDisabled ? "text-gray-500" : "text-primary-500"}`}
                          />
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`font-medium truncate ${isDisabled ? "text-gray-500" : "text-secondary-200"}`}
                            >
                              {project.name}
                            </div>
                            {project.isLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
                                Linked Copy
                              </span>
                            )}
                            {project.hasLinkedCopy && (
                              <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-500/20 text-red-400 rounded-full">
                                Has linked copy
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isSelected && (
                            <span className="text-xs font-semibold text-primary-500">Selected</span>
                          )}
                          {!isDisabled && <MdChevronRight className="w-5 h-5 text-secondary-200" />}
                        </div>
                      </button>
                    );
                  })}
                {/* Projects load more indicator */}
                {activeTab === "projects" && projects.length > 0 && hasMoreProjects && (
                  <div ref={projectsLoadMoreRef} className="flex justify-center py-8">
                    {projectsLoading && (
                      <div className="flex items-center gap-2 text-muted">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span>Loading more projects...</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-800">
          <Button variant="secondary" size="sm" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleDuplicate}
            disabled={!isNameValid || isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner />
                Duplicating...
              </span>
            ) : (
              "Duplicate"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
